import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Alert, Spacing, Text } from '@reservamos/elements';
import Loader from 'ui/atoms/Loader';
import SectionTitle from 'ui/atoms/SectionTitle';
import ExchangeLineSelector, { getLineKey } from './ExchangeLineSelector';
import IAMSAForm from './IAMSAForm';
import GfaForm from './GfaForm';
import SNForm from './SNForm';
import CDSForm from './CDSForm';
import ExchangeFormLayout from './ExchangeFormLayout';
import GfaDisclaimer from './GfaDisclaimer';
import SNDisclaimer from './SNDisclaimer';

const components = {
  'gfa': GfaForm,
  'roll-bits': GfaForm,
  'satelitenorte': SNForm,
  'saritur': SNForm,
  'empresaunida': SNForm,
  'expressosaoluiz': SNForm,
  'rochoa': SNForm,
  'cds': CDSForm,
  'default': IAMSAForm,
};

// Informative copy the redesigned shell shows below the form, keyed exactly like `components` so
// a brand's form and its rules are resolved by the same decision. A brand absent from here shows
// no copy: the area stays, empty, rather than borrowing another brand's rules.
const disclaimers = {
  'gfa': GfaDisclaimer,
  'roll-bits': GfaDisclaimer,
  'satelitenorte': SNDisclaimer,
  'saritur': SNDisclaimer,
  'empresaunida': SNDisclaimer,
  'expressosaoluiz': SNDisclaimer,
  'rochoa': SNDisclaimer,
};

// What the panel beside the form says the ticket carries, keyed like `components`. The SN family
// asks for a ticket number, a document and an email — never the operation number and NIT the
// default caption names — so the panel would otherwise point travellers at data their form does
// not mention. A brand absent from here gets the default.
const slideTicketCaptions = {
  satelitenorte: 'form_slides.on_your_ticket_number',
  saritur: 'form_slides.on_your_ticket_number',
  empresaunida: 'form_slides.on_your_ticket_number',
  expressosaoluiz: 'form_slides.on_your_ticket_number',
  rochoa: 'form_slides.on_your_ticket_number',
};

const propTypes = {
  handleSubmit: PropTypes.func,
  lineSelectionEnabled: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  lines: PropTypes.arrayOf(PropTypes.object),
  linesLoading: PropTypes.bool,
  linesError: PropTypes.string,
};

/**
 * Picks the exchange form to render and, for tenants that resolve their line explicitly
 * (EXCHANGE_LINE_SELECTION_ENABLED), owns the line selection that sits above it.
 *
 * The chosen line decides the form: each line can ask for different data, so the selector can't
 * live inside any one form. The transporter is injected into the submit here, which is why none
 * of the forms had to learn about lines.
 *
 * The brand map (and, when the tenant selects a line, the chosen line) always decides *which*
 * form renders. `USE_NEW_EXCHANGE_UI` only decides *where*: with the flag on, whatever this
 * resolves to is mounted inside the redesigned shell's #left-content, which is a container for
 * the tenant's own form.
 * @param {Object} props - Component props
 * @param {Function} props.handleSubmit - Submit handler the rendered form calls with its values
 * @param {Boolean} props.lineSelectionEnabled - Whether this tenant resolves its line explicitly
 * @param {Object[]} props.lines - Exchangeable lines for the current tenant
 * @param {Boolean} props.linesLoading - Whether `lines` is still being fetched
 * @param {String} props.linesError - Error message from fetching `lines`, if any
 * @returns Component
 */
const FormLayout = ({
  handleSubmit,
  lineSelectionEnabled = false,
  lines = [],
  linesLoading = false,
  linesError = null,
  ...formProps
}) => {
  const { t } = useTranslation('exchange');
  const { env, features } = useSelector((state) => state.whitelabelConfig);
  const [selectedLineKey, setSelectedLineKey] = useState(null);

  const brandForm = components[env.brand] ?? components.default;
  // Which form is rendered is decided in `renderForm`, and for a tenant that picks its line it is
  // only known there. Captured on the way past so the footer can show that form's own copy.
  let renderedFormKey = components[env.brand] ? env.brand : null;

  /**
   * Resolves what stands in the form's place: the tenant's form, or the line selection that
   * decides which form that is.
   * @returns {JSX.Element} - The tenant's form, the line selection above it, or the state that
   * replaces both while the lines are unknown or unavailable
   */
  const renderForm = () => {
    if (!lineSelectionEnabled) {
      // Every tenant that exchanges through `tenant.exchange_transporter`: same form, same submit,
      // no line state at all.
      const Brand = brandForm;
      // eslint-disable-next-line react/jsx-props-no-spreading
      return <Brand handleSubmit={handleSubmit} {...formProps} />;
    }

    // Picking a line comes before the form, so the title moves above the selector and the form
    // drops its own. Each form still renders its title for every tenant without the selector.
    const lineSelectionTitle = <SectionTitle title={t('title.line_selection')} />;

    if (linesLoading) {
      return (
        <Spacing vertical responsiveSize="XL">
          {lineSelectionTitle}
          <Loader isLoading />
        </Spacing>
      );
    }
    if (linesError) {
      return (
        <Spacing vertical responsiveSize="XL">
          {lineSelectionTitle}
          <Alert alertType="error" title={t('info.lines_load_failed')} />
        </Spacing>
      );
    }
    // A documented 200 meaning exchange isn't available for this tenant, not a failure — and
    // nothing to exchange against, so no form is offered.
    if (!lines.length) {
      return (
        <Spacing vertical responsiveSize="XL">
          {lineSelectionTitle}
          <Alert alertType="info" title={t('info.exchange_unavailable')} />
        </Spacing>
      );
    }

    // The first line starts chosen, so the form shows without an extra click; the traveller can
    // still pick another. If the chosen line drops out of a refreshed list, it falls back to the
    // first again. The line always travels explicitly: the resolver only falls back to
    // `tenant.exchange_transporter` and this tenant has none on purpose.
    // The selection is held by line, not by abbr — lines can share a transporter — and only the
    // abbr travels from here on.
    const selectedLine = lines.find((line) => getLineKey(line) === selectedLineKey) ?? lines[0];
    const transporter = selectedLine?.transporterAbbr;
    // Falls back to the brand's form for any line the map doesn't name — the IAMSA family shares
    // one form.
    const Component = (transporter && components[transporter]) ?? brandForm;
    renderedFormKey = transporter && components[transporter] ? transporter : renderedFormKey;

    /**
     * Adds the chosen line to whatever the rendered form submits.
     * @param {Object} values - Values built by the form
     * @returns {*} - Whatever the caller's handler returns
     */
    const handleSubmitWithTransporter = (values) => handleSubmit({ ...values, transporter });

    return (
      <Spacing vertical responsiveSize="XL">
        {lineSelectionTitle}
        <ExchangeLineSelector
          lines={lines}
          value={selectedLine ? getLineKey(selectedLine) : null}
          onChange={setSelectedLineKey}
          label={t('labels.select_line')}
        />
        {transporter && (
          <Spacing vertical size="S">
            <Text>{t('subtitle.input_required_data')}</Text>
            <Component
              handleSubmit={handleSubmitWithTransporter}
              hideTitle
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...formProps}
            />
          </Spacing>
        )}
      </Spacing>
    );
  };

  const form = renderForm();

  if (features.USE_NEW_EXCHANGE_UI) {
    const Disclaimer = renderedFormKey ? disclaimers[renderedFormKey] : null;
    return (
      <ExchangeFormLayout
        disclaimer={Disclaimer ? <Disclaimer weight="regular" /> : null}
        slidesTicketCaptionKey={renderedFormKey ? slideTicketCaptions[renderedFormKey] : undefined}
      >
        {form}
      </ExchangeFormLayout>
    );
  }

  return form;
};

FormLayout.propTypes = propTypes;

export default FormLayout;
